import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'

import { ICrudFetchOptions } from 'clickNgo/models/crud/utils'
import { IDataProviderFilter, IFilter, Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'

import { IPartner } from '../models'

type TParams = {
  topPartnerId: string
  searchQuery?: string
  individualFilters: Partial<Record<keyof IPartner, string>>
  withoutInactive?: boolean
  withoutSelf?: boolean
  columns?: 'default' | 'with_legal' | 'only_legal' | 'only_open'
}

export const legalColumns = [
  { field: 'TopPartnerID', title: 'TopPartnerID', priority: 1 },
  { field: 'PartnerID', title: 'PartnerID', priority: 2 },
  {
    field: 'LegalWorkflowStatusExt',
    title: 'Legal Workflow Status',
    priority: 3,
  },
  {
    field: 'LegalModifiedValuesExt',
    title: 'Legal Modified Values',
    priority: 4,
  },
  { field: 'CompanyLegalName', title: 'Company Legal Name', priority: 5 },
  {
    field: 'CompanyLegalNameNonLatin',
    title: 'Company Legal Name Non Latin',
    priority: 6,
  },
  { field: 'LegalAddr1', title: 'Legal Address 1', priority: 7 },
  { field: 'LegalAddr2', title: 'Legal Address 2', priority: 8 },
  { field: 'LegalAddr3', title: 'Legal Address 3', priority: 9 },
  { field: 'LegalCity', title: 'Legal City', priority: 10 },
  { field: 'LegalState', title: 'Legal State', priority: 11 },
  { field: 'LegalCountry', title: 'Legal Country', priority: 12 },
  { field: 'LegalPostalCode', title: 'Legal Postal Code', priority: 13 },
  { field: 'Market', title: 'Market', priority: 14 },
  { field: 'Country', title: 'Country', priority: 15 },
  {
    field: 'LegalApproverComment',
    title: 'Legal Approver Comment',
    priority: 16,
  },
  { field: 'LegalModified', title: 'LegalModified', priority: 17 },
  {
    field: 'LegalProfilingComment',
    title: 'Legal Profiling Comment',
    priority: 18,
  },
]

export const notLegalColumns = [
  { field: 'HierarchyType', title: 'Hierarchy Type', priority: 1 },
  { field: 'CommunicationName', title: 'Comm. Name', priority: 2 },
  { field: 'PartnerID', title: 'Partner ID', priority: 3 },
  { field: 'ParentPartnerID', title: 'Parent Partner ID', priority: 4 },
  { field: 'PartnerType', title: 'Partner Type', priority: 5 },
  { field: 'LocationID', title: 'Location ID', priority: 6 },
  { field: 'MainPhone', title: 'Phone', priority: 7 },
  { field: 'City', title: 'City', priority: 8 },
  { field: 'CompanyLegalName', title: 'Legal Name', priority: 9 },
  { field: 'TaxID', title: 'Tax ID', priority: 10 },
  { field: 'ZIPCode', title: 'ZIPCode', priority: 11 },
  { field: 'AddressLine1', title: 'Address Line 1', priority: 12 },
  { field: 'AddressLine2', title: 'Address Line 2', priority: 13 },
  { field: 'AddressLine3', title: 'Address Line 3', priority: 14 },
  { field: 'State', title: 'State', priority: 15 },
  { field: 'Region', title: 'Region', priority: 16 },
  { field: 'StoreNumber', title: 'Store Number', priority: 17 },
]

export const onlyOpenedColumns = [
  { field: 'Country', title: 'Country', priority: 15 },
  { field: 'Market', title: 'Market', priority: 15 },
  { field: 'ModifiedBy', title: 'Modified By', priority: 16 },
  { field: 'Modified', title: 'Modified', priority: 17 },
  { field: 'WorkflowStatus', title: 'Workflow status', priority: 2 },
  {
    field: 'ProfileTeamComment',
    title: 'Profile team comment',
    priority: 2,
  },
]

const columnCollection = {
  default: notLegalColumns,
  with_legal: [...notLegalColumns, ...legalColumns],
  only_legal: legalColumns,
  only_open: onlyOpenedColumns,
}

export function exportPartnerHierarchy(
  {
    topPartnerId,
    searchQuery = '',
    individualFilters,
    withoutInactive,
    withoutSelf,
    columns = 'default',
  }: TParams,
  options?: ICrudFetchOptions
) {
  const source = CrudStore.get(
    CrudStoreItem.PartnerAllData,
    options?.cacheKey || `partnerHierarchy-${topPartnerId}`
  )
  const filterByTopPartnerId = {
    field: 'TopPartnerID',
    operator: Operator.EQUAL,
    value: topPartnerId,
  }
  const filterByActiveStatus = {
    logic: Logic.OR,
    filters: [
      {
        field: 'StatusExt',
        operator: Operator.EQUAL,
        value: 'Active',
      },
      {
        field: 'StatusExt',
        operator: Operator.ISNULL,
        value: '',
      },
    ],
  }
  const filterByNotSelf = {
    field: 'PartnerID',
    operator: Operator.NOTEQUAL,
    value: topPartnerId,
  }

  const filterBySearchQuery = {
    logic: Logic.OR,
    filters: [
      {
        field: 'PartnerID',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
      {
        field: 'CommunicationName',
        operator: Operator.CONTAINS,
        value: searchQuery,
      },
    ],
  }

  const filterByIndividualFilters = {
    logic: Logic.AND,
    filters: [
      {
        field: 'PartnerID',
        operator: Operator.CONTAINS,
        value: individualFilters.partnerId || '',
      },
      {
        field: 'HierarchyType',
        operator: Operator.CONTAINS,
        value: individualFilters.hierarchyType || '',
      },
      {
        field: 'CommunicationName',
        operator: Operator.CONTAINS,
        value: individualFilters.communicationName || '',
      },
    ].filter((el) => el.value),
  }

  const initialFilters: Array<IDataProviderFilter | IFilter> = [filterByTopPartnerId]

  if (withoutInactive) {
    initialFilters.push(filterByActiveStatus)
  }

  if (withoutSelf) {
    initialFilters.push(filterByNotSelf)
  }

  if (searchQuery) {
    initialFilters.push(filterBySearchQuery)
  }

  if (filterByIndividualFilters.filters.some((el) => el.value)) {
    initialFilters.push(filterByIndividualFilters)
  }

  const filters = {
    logic: Logic.AND,
    filters: initialFilters,
  }

  source.setFilter(filters)

  source.setPaging({
    page: 0,
    pageSize: 100000,
  })

  return source.export(`contacts_of_${topPartnerId}`, columnCollection[columns])
}
